import { Fuel, VehicleModelWithInsurancesForD2CDto } from '@orcar/common';
import {
  convertToKST,
  filterOptionAtom,
  PAGE_NAME,
  SearchParamsType,
  SearchResultBlock,
  SearchResultBlockDivider,
  SearchResultBlockFooter,
  SearchResultBlockHeader,
  SESSION_STORAGE_KEY,
  TabList,
  useRecoil,
} from '@orcar/common-d2c';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CAR_TYPE } from '@/constants/vehicle-model';
import SearchFilterNull from './vehicleCard/SearchFilterNull';

const SORT_OPTIONS = [
  { id: 'popular', name: '인기순' },
  { id: 'price', name: '최저가순' },
  { id: 'available', name: '예약 가능순' },
];

export type SearchResultProps = {
  searchOptions: SearchParamsType;
  vehicleModelList?: VehicleModelWithInsurancesForD2CDto[];
};

const SearchResult = ({
  searchOptions,
  vehicleModelList,
}: SearchResultProps) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [fadeInTrigger, setFadeInTrigger] = useState(false);
  const [sortBy, setSortBy] = useState('popular'); // 기본값: 인기순
  const {
    recoilState: [recoilFilterOption, setRecoilFilterOption],
  } = useRecoil(filterOptionAtom);

  // 정렬된 차량 목록
  const sortedVehicleList = vehicleModelList
    ? [...vehicleModelList].sort((a, b) => {
        // 모든 정렬에서 예약 가능한 차량을 우선으로 정렬
        if (a.availableCount === 0 && b.availableCount > 0) return 1;
        if (a.availableCount > 0 && b.availableCount === 0) return -1;

        switch (sortBy) {
          case 'price':
            // 최저가순: 기본 보험의 최종 가격으로 정렬
            const priceA = a.insurances[0]?.finalPrice || 0;
            const priceB = b.insurances[0]?.finalPrice || 0;
            return priceA - priceB;
          case 'available':
            // 예약 가능순: 예약 가능한 차량을 우선으로 정렬
            return b.availableCount - a.availableCount;
          case 'popular':
          default:
            // 기본 순서: 서버에서 최신 연식 + 가격 순으로 정렬해서 보내줌
            return 0;
        }
      })
    : vehicleModelList;

  useEffect(() => {
    setFadeInTrigger(true);
    setTimeout(() => {
      setFadeInTrigger(false);
    }, 100);
  }, [recoilFilterOption, sortBy]);

  useEffect(() => {
    const carType = searchParams.get('carType');
    const carFuel = searchParams.get('carFuel');
    const sort = searchParams.get('sort');

    if (carType || carFuel || sort) {
      const newFilterOption = { ...recoilFilterOption };

      if (carType) {
        newFilterOption.carTypeList = [carType];
      }

      if (carFuel) {
        newFilterOption.carFuelList = [carFuel];
      }

      if (sort) {
        setSortBy(sort);
      }

      setRecoilFilterOption(newFilterOption);
    }
  }, [searchParams, setRecoilFilterOption]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);

    if (recoilFilterOption.carTypeList.length === 1) {
      const carType = recoilFilterOption.carTypeList[0];
      newSearchParams.set('carType', carType);
    } else {
      newSearchParams.delete('carType');
    }

    if (
      recoilFilterOption.carFuelList.length === 1 &&
      recoilFilterOption.carFuelList.includes(Fuel.EV)
    ) {
      newSearchParams.set('carFuel', Fuel.EV);
    } else {
      newSearchParams.delete('carFuel');
    }

    // 정렬 파라미터 추가
    if (sortBy !== 'popular') {
      newSearchParams.set('sort', sortBy);
    } else {
      newSearchParams.delete('sort');
    }

    setSearchParams(newSearchParams, { replace: true });
  }, [
    recoilFilterOption.carTypeList,
    recoilFilterOption.carFuelList,
    sortBy,
    searchParams,
    setSearchParams,
  ]);

  const handleSelectProducts = async (id: number, insuranceName: string) => {
    sessionStorage.setItem(SESSION_STORAGE_KEY.PRODUCT_ID, String(id));
    const pickUpAt = convertToKST(searchOptions.pickUpAt);
    const dropOffAt = convertToKST(searchOptions.dropOffAt);

    navigate(
      `${PAGE_NAME.order_confirmation}?pickUpAt=${pickUpAt}&dropOffAt=${dropOffAt}&insuranceName=${insuranceName}`,
    );
  };

  const handleSortChange = (sortId: string) => {
    setSortBy(sortId);
  };

  return (
    <div className='flex w-[1071px] flex-col justify-start rounded-t-m bg-grey-000'>
      <div className='sticky top-[200px] z-10 rounded-m bg-grey-000'>
        <SearchResultBlockHeader />
        <div className='border-x border-grey-010'>
          <TabList tabList={CAR_TYPE} />
        </div>
        {/* 정렬 옵션 */}
        <div className='p-4'>
          <div className='flex gap-4'>
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => handleSortChange(option.id)}
                className={`body2 rounded px-3 py-1 transition-colors ${
                  sortBy === option.id
                    ? 'bg-primary-050 text-white'
                    : 'bg-transparent text-grey-070 hover:text-primary-050'
                }`}
              >
                {option.name}
              </button>
            ))}
          </div>
        </div>
      </div>
      {sortedVehicleList?.length === 0 ? (
        <SearchFilterNull />
      ) : (
        sortedVehicleList?.map((vehicleModel, idx) => (
          <Fragment key={vehicleModel.id}>
            <SearchResultBlock
              platform='desktop'
              fadeInTrigger={fadeInTrigger}
              vehicleModel={vehicleModel}
              unavailable={vehicleModel.availableCount === 0}
              customSuperInsuranceName='고급자차 + 무제한특약'
              handleClickCard={handleSelectProducts}
              hideDeductible
              hideNoneInsurance
            />
            {idx < sortedVehicleList.length - 1 && <SearchResultBlockDivider />}
          </Fragment>
        ))
      )}
      <SearchResultBlockFooter />
    </div>
  );
};

export default SearchResult;
